/**
 * ============================================================================
 * API СЕРВИС ДЛЯ УПРАВЛЕНИЯ ПОЛЬЗОВАТЕЛЯМИ
 * ============================================================================
 * Методы для работы с пользователями (CRUD операции)
 * Доступно только для администраторов
 * ============================================================================
 */

import api from './api'
import { compressImage, shouldCompress } from '@/utils/imageCompression'

/**
 * Получение списка всех пользователей
 * @param {Object} params - Параметры запроса
 * @param {number} params.page - Номер страницы
 * @param {number} params.limit - Количество на странице
 * @param {string} params.role - Фильтр по роли
 * @param {string} params.status - Фильтр по статусу
 * @param {string} params.search - Поиск по имени или email
 * @returns {Promise<Object>} Список пользователей с пагинацией
 */
export async function getAllUsers(params = {}) {
	const queryParams = new URLSearchParams()

	if (params.page) queryParams.append('page', params.page)
	if (params.limit) queryParams.append('limit', params.limit)
	if (params.role) queryParams.append('role', params.role)
	if (params.status) queryParams.append('status', params.status)
	if (params.search) queryParams.append('search', params.search)

	const response = await api.get(`/users?${queryParams.toString()}`)
	return response.data
}

/**
 * Получение пользователя по ID
 * @param {number} userId - ID пользователя
 * @returns {Promise<Object>} Данные пользователя
 */
export async function getUserById(userId) {
	const response = await api.get(`/users/${userId}`)
	return response.data
}

/**
 * Создание нового пользователя
 * @param {Object} userData - Данные пользователя
 * @param {string} userData.email - Email
 * @param {string} userData.password - Пароль
 * @param {string} userData.full_name - Полное имя
 * @param {string} userData.role - Роль (admin, teacher, student)
 * @returns {Promise<Object>} Созданный пользователь
 */
export async function createUser(userData) {
	const response = await api.post('/users', userData)
	return response.data
}

/**
 * Обновление данных пользователя
 * @param {number} userId - ID пользователя
 * @param {Object} updates - Обновляемые данные
 * @param {string} updates.full_name - Полное имя
 * @param {string} updates.email - Email
 * @param {string} updates.role - Роль
 * @param {boolean} updates.is_active - Статус активности
 * @returns {Promise<Object>} Обновленный пользователь
 */
export async function updateUser(userId, updates) {
	const response = await api.put(`/users/${userId}`, updates)
	return response.data
}

/**
 * Блокировка/разблокировка пользователя
 * @param {number} userId - ID пользователя
 * @returns {Promise<Object>} Новый статус пользователя
 */
export async function toggleUserStatus(userId) {
	const response = await api.patch(`/users/${userId}/toggle-status`)
	return response.data
}

/**
 * Удаление пользователя
 * @param {number} userId - ID пользователя
 * @returns {Promise<Object>} Результат удаления
 */
export async function deleteUser(userId) {
	const response = await api.delete(`/users/${userId}`)
	return response.data
}

/**
 * Получение профиля текущего пользователя
 * @returns {Promise<Object>} Данные профиля
 */
export async function getCurrentProfile() {
	const response = await api.get('/users/profile')
	// api.js уже распаковывает response.data, поэтому здесь { success, data }
	return response
}

/**
 * Обновление профиля текущего пользователя
 * @param {Object} updates - Обновляемые данные
 * @returns {Promise<Object>} Обновленный профиль
 */
export async function updateCurrentProfile(updates) {
	const response = await api.put('/users/profile', updates)
	return response
}

/**
 * Загрузка аватара пользователя
 * @param {File} file - Файл изображения
 * @returns {Promise<Object>} Обновленный профиль с новым аватаром
 */
export async function uploadAvatar(file) {
	// Сжимаем изображение если оно больше 500KB (для аватаров меньший лимит)
	let fileToUpload = file
	if (shouldCompress(file, 0.5)) {
		console.log(`🗜️ Сжатие аватара: ${(file.size / 1024 / 1024).toFixed(2)}MB`)
		fileToUpload = await compressImage(file, {
			maxWidth: 800,
			maxHeight: 800,
			quality: 0.85,
			maxSizeMB: 0.5,
		})
		console.log(`✅ Сжато до: ${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`)
	}

	const formData = new FormData()
	formData.append('avatar', fileToUpload)

	const response = await api.post('/users/profile/avatar', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	})
	return response
}

/**
 * Удаление аватара пользователя
 * @returns {Promise<Object>} Обновленный профиль без аватара
 */
export async function deleteAvatar() {
	const response = await api.delete('/users/profile/avatar')
	return response
}

export default {
	getAllUsers,
	getUserById,
	createUser,
	updateUser,
	toggleUserStatus,
	deleteUser,
	getCurrentProfile,
	updateCurrentProfile,
	uploadAvatar,
	deleteAvatar,
}
